import React, { useState } from 'react';
import { compareEs } from '../../services/utils';

const DataTable = ({ columns, data, emptyMessage = "No hay datos disponibles", sortable = true, rowClassName, compact = false }) => {
  const cellPadding = compact ? 'px-3' : 'px-6';
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (columnKey) => {
    if (!sortable || !columns.find(col => col.key === columnKey)?.sortable) {
      return;
    }

    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const sortedData = React.useMemo(() => {
    if (!sortColumn || !sortable) return data;

    return [...data].sort((a, b) => {
      // Buscar la columna para obtener el sortKey si existe
      const column = columns.find(col => col.key === sortColumn);
      const sortKey = column?.sortKey || column?.key;
      
      const aValue = column?.sortValue ? column.sortValue(a) : a[sortKey];
      const bValue = column?.sortValue ? column.sortValue(b) : b[sortKey];

      const result = typeof aValue === 'number' && typeof bValue === 'number'
        ? aValue - bValue
        : compareEs(String(aValue ?? ''), String(bValue ?? ''));

      return sortDirection === 'asc' ? result : -result;
    });
  }, [data, sortColumn, sortDirection, sortable, columns]);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                onClick={() => handleSort(column.key)}
                className={`${cellPadding} py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                  sortable && column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                }`}
              >
                <div className="flex items-center">
                  {column.label}
                  {sortColumn === column.key && (
                    <span className="ml-1">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedData.length > 0 ? (
            sortedData.map((row, rowIndex) => {
              const rowClasses = rowClassName ? rowClassName(row) : '';
              return (
                <tr key={rowIndex} className={`hover:bg-gray-50 ${rowClasses}`}>
                  {columns.map((column) => (
                    <td key={column.key} className={`${cellPadding} py-4 whitespace-nowrap text-sm ${rowClasses ? 'text-gray-500' : 'text-gray-900'}`}>
                      {column.render ? column.render(row[column.key], row) : row[column.key]}
                    </td>
                  ))}
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;